import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { RutField, fillEmpty } from './RutField';
import { toast } from 'sonner';
import { Radar, Loader2, AlertTriangle } from 'lucide-react';

const INPUT = 'w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none';

const LinkToMatter = ({ cause, matters }) => {
  const { api } = useAuth();
  const [id, setId] = useState('');
  const link = async () => {
    if (!id) return toast.error('Elige un caso');
    try {
      const { data } = await api.post(`/crm/matters/${id}/link-cause`, { cause: { ...cause, source: 'boostr' } });
      toast.success(data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo vincular');
    }
  };
  if (!matters.length) return null;
  return (
    <div className="flex gap-2 mt-2">
      <select data-testid="link-cause-matter-select" value={id} onChange={(e) => setId(e.target.value)} className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-white">
        <option value="">Vincular a un caso del CRM...</option>
        {matters.map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
      </select>
      <button data-testid="link-cause-btn" onClick={link} className="text-xs bg-cyan-600 text-white rounded-lg px-3">Vincular</button>
    </div>
  );
};

const CauseItem = ({ c, matters }) => (
  <div className="p-3 bg-slate-800/60 rounded-lg border border-slate-700">
    <div className="flex items-center justify-between gap-2">
      <p className="font-mono text-cyan-400 text-sm">{c.rit || c.rol}</p>
      <span className="text-xs text-slate-400">{c.status}</span>
    </div>
    <p className="text-sm text-white">{c.labeled}</p>
    <p className="text-xs text-slate-500">{c.court} · {c.date}</p>
    <LinkToMatter cause={c} matters={matters} />
  </div>
);

export const BoostrSearch = () => {
  const { api } = useAuth();
  const [f, setF] = useState({ rut: '', nombre: '', apellido: '', segundo_apellido: '', tipo: 'natural', competencia: 'civil' });
  const [op, setOp] = useState(null);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);
  const timer = useRef(null);
  const [matters, setMatters] = useState([]);
  useEffect(() => { api.get('/crm/matters?limit=100').then((r) => setMatters(r.data.matters || [])).catch(() => {}); }, [api]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => () => clearTimeout(timer.current), []);

  const poll = async (id) => {
    try {
      const { data } = await api.get(`/poder-judicial/boostr/operations/${id}`);
      setOp(data);
      if (data.status !== 'done') timer.current = setTimeout(() => poll(id), 10000);
    } catch {
      timer.current = setTimeout(() => poll(id), 15000);
    }
  };

  const start = async (e) => {
    e.preventDefault();
    setError('');
    setStarting(true);
    try {
      const { data } = await api.post('/poder-judicial/boostr/search', f);
      if (!data.success) {
        setError(data.message);
        return;
      }
      toast.success(data.message);
      setOp({ operation_id: data.operation_id, status: 'processing', causes: [] });
      poll(data.operation_id);
    } catch (err) {
      setError(err.response?.data?.detail || 'No se pudo iniciar la consulta');
    } finally {
      setStarting(false);
    }
  };

  return (
    <div data-testid="boostr-search-section" className="bg-card border border-cyan-500/30 rounded-xl p-6 mb-6">
      <h3 className="font-semibold text-white flex items-center gap-2 mb-1"><Radar className="w-5 h-5 text-cyan-400" />Consulta real en el Poder Judicial (Boostr)</h3>
      <p className="text-xs text-slate-400 mb-4">Busca todas las causas de una persona o empresa en los tribunales de Chile (excepto familia y causas reservadas). Tarda unos minutos.</p>
      <form onSubmit={start} className="grid grid-cols-1 md:grid-cols-6 gap-3">
        <RutField testid="boostr-rut-input" value={f.rut} onChange={(v) => setF((x) => ({ ...x, rut: v }))} placeholder="RUT" className={INPUT} required
          onLookup={(d) => setF((x) => {
            const t = (d.name || '').trim().split(/\s+/);
            return x.tipo === 'juridica' || t.length < 3 ? fillEmpty(x, { nombre: d.name }) : fillEmpty(x, { nombre: t.slice(0, -2).join(' '), apellido: t[t.length - 2], segundo_apellido: t[t.length - 1] });
          })} />
        <input data-testid="boostr-name-input" value={f.nombre} onChange={set('nombre')} placeholder={f.tipo === 'juridica' ? 'Razón social' : 'Nombres'} className={INPUT} required />
        {f.tipo === 'natural' && <input data-testid="boostr-lastname-input" value={f.apellido} onChange={set('apellido')} placeholder="Primer apellido" className={INPUT} />}
        {f.tipo === 'natural' && <input data-testid="boostr-second-lastname-input" value={f.segundo_apellido} onChange={set('segundo_apellido')} placeholder="Segundo apellido" className={INPUT} />}
        <select data-testid="boostr-type-select" value={f.tipo} onChange={set('tipo')} className={INPUT}>
          <option value="natural">Persona</option>
          <option value="juridica">Empresa</option>
        </select>
        <select data-testid="boostr-context-select" value={f.competencia} onChange={set('competencia')} className={INPUT}>
          <option value="civil">Civil</option>
          <option value="penal">Penal</option>
          <option value="apelacion">Cortes de Apelaciones</option>
        </select>
        <button data-testid="boostr-search-btn" type="submit" disabled={starting} className="md:col-span-6 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold py-2 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50">
          {starting && <Loader2 className="w-4 h-4 animate-spin" />}Consultar causas reales
        </button>
      </form>
      {error && (
        <p data-testid="boostr-error" className="mt-4 text-sm text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{error}
        </p>
      )}
      {op && (
        <div data-testid="boostr-operation" className="mt-4 space-y-2">
          <p className="text-sm text-slate-300 flex items-center gap-2">
            {op.status !== 'done' && <Loader2 className="w-4 h-4 animate-spin text-cyan-400" />}
            {op.status === 'done' ? `${op.causes.length} causa(s) encontradas` : `Consultando tribunales... ${op.progress ? `${op.progress}%` : ''}`}
          </p>
          {op.causes.map((c, i) => <CauseItem key={`${c.rit}-${i}`} c={c} matters={matters} />)}
        </div>
      )}
    </div>
  );
};
